import React, { useState, useEffect } from 'react';
import { PolaroidPosition } from '../types';

interface PolaroidProps {
  data: PolaroidPosition;
  isMounted: boolean;
  isActive: boolean;
  onClick: () => void;
}

export const Polaroid: React.FC<PolaroidProps> = ({ data, isMounted, isActive, onClick }) => {
  // Use local state to handle the transition from start pos to end pos
  const [position, setPosition] = useState({ top: data.startTop, left: data.startLeft, rotation: 0 });
  const [opacity, setOpacity] = useState(0);
  const [hasEntered, setHasEntered] = useState(false);

  useEffect(() => {
    if (isMounted && !hasEntered) {
      // Trigger the slide-in animation after the mount
      const timeout = setTimeout(() => {
        setPosition({ top: data.top, left: data.left, rotation: data.rotation });
        setOpacity(1);
        setHasEntered(true);
      }, data.delay * 1000);

      return () => clearTimeout(timeout);
    }
  }, [isMounted, data.top, data.left, data.rotation, data.delay, hasEntered]);

  // Determine aspect ratio class based on orientation
  const aspectRatioClass = data.orientation === 'landscape' ? 'aspect-[5/4]' : 'aspect-[4/5]';
  
  // Base width styling
  const widthStyle = data.orientation === 'landscape' 
    ? 'clamp(180px, 18vw, 280px)' 
    : 'clamp(150px, 15vw, 250px)';

  // Calculate dynamic styles based on active state
  const currentStyle = isActive ? {
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) rotate(0deg) scale(1.3)',
    opacity: 1,
    zIndex: 99999, // Ensure it's on top of everything
    width: widthStyle,
    cursor: 'default',
  } : {
    top: `${position.top}%`,
    left: `${position.left}%`,
    transform: `translate(-50%, -50%) rotate(${position.rotation}deg)`,
    opacity: opacity,
    zIndex: data.zIndex,
    width: widthStyle,
    cursor: 'pointer',
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent bubbling to background
    onClick();
  };

  return (
    <div
      className="absolute bg-white shadow-xl p-3 pb-8 transition-all duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-105"
      style={currentStyle}
      onClick={handleClick}
    >
      <div className={`${aspectRatioClass} w-full overflow-hidden bg-gray-100`}>
        <img
          src={data.url}
          alt={data.caption}
          className="w-full h-full object-cover pointer-events-none" // prevent img drag
          loading="lazy"
        />
      </div>
      {/* Handwritten Caption */}
      <div className="mt-3 text-center text-gray-700 font-handwritten text-xl tracking-tight">
        {data.caption}
      </div>
    </div>
  );
};